/*
 * Maria's Whiskey Club — Master Bottling Chronicle
 * Ported from G:\fabula-video\omniverse\src\universes\MariasBottling\index.tsx
 * (React/canvas demo build) to a vanilla-JS theme asset. All canvas drawing,
 * WebAudio sensory engine, and scroll-hijack logic is verbatim from the
 * source's imperative useEffect body — only React state/refs were swapped
 * for plain closures. Bootstraps itself on DOMContentLoaded and re-inits on
 * shopify:section:load for theme-editor compatibility.
 */
const BOTTLE_NOTES = {
  _default: { nose: ["MADAGASCAR VANILLA", "CHARRED OAK"], palate: ["DARK CHERRY", "SALTED CARAMEL"], finish: ["MILD PEAT SMOKE", "RICH LEATHER"] },
  "green-river-wheated": { nose: ["VANILLA", "WILDFLOWER HONEY"], palate: ["GRILLED PEACH", "DRIZZLED CARAMEL"], finish: ["GENTLE SPICE", "WARM WHEAT"] },
  "elijah-craig": { nose: ["CARAMEL", "BAKING SPICE"], palate: ["VANILLA", "MILK CHOCOLATE"], finish: ["DARK CHOCOLATE", "LONG & SWEET"] },
  "four-roses-private-select-2025": { nose: ["BAKING SPICE", "DRIED CHERRY"], palate: ["RYE SPICE", "VANILLA BEAN"], finish: ["CLOVE", "ORANGE PEEL"] },
  "blue-note-beast-of-the-east": { nose: ["CARAMEL CORN", "ORANGE ZEST"], palate: ["PRALINE", "TOASTED PECAN"], finish: ["WHITE PEPPER", "SUBTLE CHERRY"] },
  "james-pepper-decanter": { nose: ["TOASTED OAK", "WALNUT"], palate: ["DRIED DARK FRUIT", "MARZIPAN"], finish: ["CREAMY VANILLA", "NUTMEG"] },
  "woodford-reserve-110-proof": { nose: ["TOASTED GRAIN", "WARM OAK"], palate: ["ROASTED NUTS", "CARAMEL"], finish: ["BAKING SPICE", "ROUND OAK"] },
  "penelope-toasted-bourbon": { nose: ["ROASTED CORN", "SWEET VANILLA"], palate: ["BITTERSWEET COCOA", "BROWN SUGAR"], finish: ["DARK MOLASSES", "OAK CHAR"] },
  "old-forester-black-label-100-proof-single-barrel": { nose: ["BROWN SUGAR", "BANANA BREAD"], palate: ["SWEET CORN", "VANILLA"], finish: ["DRY OAK", "CITRUS ZEST"] },
  "memorial-day-kc": { nose: ["CHARRED OAK", "CREME SODA"], palate: ["CREME BRULEE", "PEANUT BRITTLE"], finish: ["BURNT CARAMEL", "TOBACCO"] },
  "buffalo-trace-2026": { nose: ["HONEY", "ORANGE PEEL"], palate: ["TOFFEE", "VANILLA CREAM"], finish: ["TOASTED OAK", "CINNAMON"] }
};
const ACTIVE_NOTES = (() => {
  try {
    const s = new URLSearchParams(window.location.search).get("bottle") || "_default";
    return BOTTLE_NOTES[s] || BOTTLE_NOTES._default;
  } catch {
    return BOTTLE_NOTES._default;
  }
})();
function initBottlingChronicle(root) {
  const canvas = root.querySelector(".mbo-canvas");
  const ctx = canvas.getContext("2d");
  const q = (sel) => root.querySelector(sel);
  const elProgressBar = q(".mbo-progress-bar");
  const elMetricPct = q(".mbo-metric-pct");
  const elSlider = q(".mbo-timeline-slider");
  const elChapterRoman = q(".mbo-chapter-roman");
  const elChapterName = q(".mbo-chapter-name");
  const elCapHeadline = q(".mbo-cap-headline");
  const elCapText = q(".mbo-cap-text");
  const elScrollCue = q(".mbo-scroll-cue");
  const sensoryBtn = q(".mbo-sensory-btn");
  const autoplayBtn = q(".mbo-autoplay-btn");
  const playIcon = q(".mbo-play-icon");
  const pauseIcon = q(".mbo-pause-icon");
  let currentViewMode = "chronicle";
  const dockButtons = Array.from(root.querySelectorAll(".mbo-dock-view-btn"));
  function setViewMode(next) {
    currentViewMode = next;
    dockButtons.forEach((btn) => btn.classList.toggle("active", btn.dataset.mode === next));
    window.dispatchEvent(new CustomEvent("reset-timeline"));
  }
  dockButtons.forEach((btn) => {
    btn.addEventListener("click", () => setViewMode(btn.dataset.mode));
  });
  let deepLinkTimeout;
  try {
    const m = new URLSearchParams(window.location.search).get("mode");
    if (m && m !== "chronicle") {
      deepLinkTimeout = window.setTimeout(() => setViewMode(m), 800);
    }
  } catch {
  }
  let W, H, CX, CY, sc;
  function drawRoundRect(ctx2, x, y, width, height, radius = 0) {
    if (typeof radius === "number") {
      radius = { tl: radius, tr: radius, br: radius, bl: radius };
    } else {
      const defaultRadius = { tl: 0, tr: 0, br: 0, bl: 0 };
      for (const side in defaultRadius) {
        radius[side] = radius[side] || defaultRadius[side];
      }
    }
    ctx2.beginPath();
    ctx2.moveTo(x + radius.tl, y);
    ctx2.lineTo(x + width - radius.tr, y);
    ctx2.quadraticCurveTo(x + width, y, x + width, y + radius.tr);
    ctx2.lineTo(x + width, y + height - radius.br);
    ctx2.quadraticCurveTo(x + width, y + height, x + width - radius.br, y + height);
    ctx2.lineTo(x + radius.bl, y + height);
    ctx2.quadraticCurveTo(x, y + height, x, y + height - radius.bl);
    ctx2.lineTo(x, y + radius.tl);
    ctx2.quadraticCurveTo(x, y, x + radius.tl, y);
    ctx2.closePath();
  }
  function adaptCanvasResolution() {
    const dpr = window.devicePixelRatio || 1;
    W = window.innerWidth;
    H = window.innerHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    CX = W / 2;
    CY = H / 2;
    sc = Math.min(W, H) / 800;
  }
  adaptCanvasResolution();
  const resizeHandler = () => {
    adaptCanvasResolution();
    renderChronicle(renderProg);
  };
  window.addEventListener("resize", resizeHandler);
  let mx = CX, my = CY;
  const globalMouseMoveHandler = (e) => {
    mx = e.clientX;
    my = e.clientY;
  };
  document.addEventListener("mousemove", globalMouseMoveHandler);
  const eOutCubic = (t) => 1 - Math.pow(1 - t, 3);
  const eInOutQuint = (t) => t < 0.5 ? 16 * t * t * t * t * t : 1 - Math.pow(-2 * t + 2, 5) / 2;
  const eElasticOvershoot = (t) => {
    const c4 = 2 * Math.PI / 3;
    return t === 0 ? 0 : t === 1 ? 1 : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
  };
  function lerp(a, b, t) {
    return a + (b - a) * t;
  }
  function clamp(v, minVal, maxVal) {
    return Math.max(minVal, Math.min(maxVal, v));
  }
  function mapRange(v, a, b, c, d) {
    return lerp(c, d, clamp((v - a) / (b - a), 0, 1));
  }
  let sfxAudioCtx = null;
  let humOsc1 = null;
  let humOsc2 = null;
  let sfxConveyorGain = null;
  let ambientFilter = null;
  let washNoiseSource = null;
  let washFilterGain = null;
  let soundEngineActive = false;
  function buildSyntheticCellarReverb(actx, inputNode, outputNode, decayTime) {
    const merger = actx.createChannelMerger(2);
    const splitter = actx.createChannelSplitter(2);
    const delayL = actx.createDelay();
    const delayR = actx.createDelay();
    delayL.delayTime.setValueAtTime(0.085, actx.currentTime);
    delayR.delayTime.setValueAtTime(0.125, actx.currentTime);
    const feedbackL = actx.createGain();
    const feedbackR = actx.createGain();
    feedbackL.gain.setValueAtTime(decayTime, actx.currentTime);
    feedbackR.gain.setValueAtTime(decayTime, actx.currentTime);
    inputNode.connect(splitter);
    splitter.connect(delayL, 0);
    splitter.connect(delayR, 1);
    delayL.connect(feedbackL);
    feedbackL.connect(delayR);
    delayR.connect(feedbackR);
    feedbackR.connect(delayL);
    delayL.connect(merger, 0, 0);
    delayR.connect(merger, 0, 1);
    merger.connect(outputNode);
  }
  function initLuxuryAcoustics() {
    if (sfxAudioCtx) return;
    sfxAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
    ambientFilter = sfxAudioCtx.createBiquadFilter();
    ambientFilter.type = "lowpass";
    ambientFilter.frequency.setValueAtTime(120, sfxAudioCtx.currentTime);
    ambientFilter.connect(sfxAudioCtx.destination);
    humOsc1 = sfxAudioCtx.createOscillator();
    humOsc1.type = "sawtooth";
    humOsc1.frequency.setValueAtTime(55, sfxAudioCtx.currentTime);
    humOsc2 = sfxAudioCtx.createOscillator();
    humOsc2.type = "sine";
    humOsc2.frequency.setValueAtTime(110.2, sfxAudioCtx.currentTime);
    sfxConveyorGain = sfxAudioCtx.createGain();
    sfxConveyorGain.gain.setValueAtTime(0.012, sfxAudioCtx.currentTime);
    const synthPreGain = sfxAudioCtx.createGain();
    synthPreGain.gain.setValueAtTime(0.15, sfxAudioCtx.currentTime);
    humOsc1.connect(synthPreGain);
    humOsc2.connect(synthPreGain);
    synthPreGain.connect(sfxConveyorGain);
    buildSyntheticCellarReverb(sfxAudioCtx, sfxConveyorGain, ambientFilter, 0.42);
    humOsc1.start();
    humOsc2.start();
    const bufferSize = sfxAudioCtx.sampleRate * 2;
    const rawNoiseBuffer = sfxAudioCtx.createBuffer(1, bufferSize, sfxAudioCtx.sampleRate);
    const chData = rawNoiseBuffer.getChannelData(0);
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + white * 0.0555179;
      b1 = 0.99332 * b1 + white * 0.0750759;
      b2 = 0.969 * b2 + white * 0.153852;
      b3 = 0.8665 * b3 + white * 0.3104856;
      b4 = 0.55 * b4 + white * 0.5329522;
      b5 = -0.7616 * b5 - white * 0.016898;
      chData[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
      chData[i] *= 0.11;
      b6 = white * 0.115926;
    }
    washNoiseSource = sfxAudioCtx.createBufferSource();
    washNoiseSource.buffer = rawNoiseBuffer;
    washNoiseSource.loop = true;
    const washBandpass = sfxAudioCtx.createBiquadFilter();
    washBandpass.type = "bandpass";
    washBandpass.frequency.setValueAtTime(450, sfxAudioCtx.currentTime);
    washBandpass.Q.setValueAtTime(3.5, sfxAudioCtx.currentTime);
    washFilterGain = sfxAudioCtx.createGain();
    washFilterGain.gain.setValueAtTime(0, sfxAudioCtx.currentTime);
    washNoiseSource.connect(washBandpass);
    washBandpass.connect(washFilterGain);
    washFilterGain.connect(sfxAudioCtx.destination);
    washNoiseSource.start();
  }
  function fireChamberImpulse(frequency, resonance, duration, peakVol) {
    if (!soundEngineActive || !sfxAudioCtx) return;
    try {
      const osc = sfxAudioCtx.createOscillator();
      const bandpass = sfxAudioCtx.createBiquadFilter();
      const env = sfxAudioCtx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(frequency, sfxAudioCtx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(frequency * 0.1, sfxAudioCtx.currentTime + duration);
      bandpass.type = "bandpass";
      bandpass.frequency.setValueAtTime(frequency, sfxAudioCtx.currentTime);
      bandpass.Q.setValueAtTime(resonance, sfxAudioCtx.currentTime);
      env.gain.setValueAtTime(peakVol, sfxAudioCtx.currentTime);
      env.gain.exponentialRampToValueAtTime(1e-3, sfxAudioCtx.currentTime + duration);
      osc.connect(bandpass);
      bandpass.connect(env);
      env.connect(sfxAudioCtx.destination);
      buildSyntheticCellarReverb(sfxAudioCtx, env, sfxAudioCtx.destination, 0.65);
      osc.start();
      osc.stop(sfxAudioCtx.currentTime + duration + 0.1);
    } catch (e) {
    }
  }
  const sensoryBtnHandler = () => {
    soundEngineActive = !soundEngineActive;
    if (soundEngineActive) {
      if (sfxAudioCtx && sfxAudioCtx.state === "suspended") {
        sfxAudioCtx.resume();
      } else {
        initLuxuryAcoustics();
      }
      sensoryBtn.classList.add("active");
      sensoryBtn.querySelector("span").textContent = "Acoustic Engine On";
      if (sfxConveyorGain && sfxAudioCtx) sfxConveyorGain.gain.setTargetAtTime(0.015, sfxAudioCtx.currentTime, 0.15);
    } else {
      sensoryBtn.classList.remove("active");
      sensoryBtn.querySelector("span").textContent = "Acoustic Engine Off";
      if (sfxConveyorGain && sfxAudioCtx) sfxConveyorGain.gain.setTargetAtTime(0, sfxAudioCtx.currentTime, 0.15);
      if (washFilterGain && sfxAudioCtx) washFilterGain.gain.setTargetAtTime(0, sfxAudioCtx.currentTime, 0.1);
    }
  };
  sensoryBtn.addEventListener("click", sensoryBtnHandler);
  const particulateArray = Array.from({ length: 90 }, () => ({
    x: Math.random(),
    y: Math.random(),
    size: Math.random() * 2.2 + 0.5,
    velocity: Math.random() * 15e-5 + 5e-5,
    alpha: Math.random() * 0.35 + 0.05,
    oscRange: Math.random() > 0.5 ? 1 : -1
  }));
  function drawBackgroundAtmosphere(p) {
    ctx.fillStyle = "#050403";
    ctx.fillRect(0, 0, W, H);
    let atmosphereGlowColor = "rgba(180, 80, 10, 0.03)";
    if (p < 0.14) {
      atmosphereGlowColor = `rgba(240, 65, 5, ${mapRange(p, 0, 0.14, 0.12, 0.18)})`;
    } else if (p < 0.28) {
      atmosphereGlowColor = `rgba(220, 50, 10, ${mapRange(p, 0.14, 0.28, 0.18, 0.1)})`;
    } else if (p < 0.42) {
      atmosphereGlowColor = `rgba(180, 70, 15, ${mapRange(p, 0.28, 0.42, 0.1, 0.03)})`;
    } else if (p < 0.56) {
      atmosphereGlowColor = `rgba(12, 172, 204, ${mapRange(p, 0.42, 0.56, 0.02, 0.08)})`;
    } else if (p < 0.7) {
      atmosphereGlowColor = `rgba(226, 140, 20, ${mapRange(p, 0.56, 0.7, 0.05, 0.12)})`;
    } else {
      atmosphereGlowColor = `rgba(226, 182, 108, 0.09)`;
    }
    const dynamicGrad = ctx.createRadialGradient(CX, CY, 0, CX, CY, Math.max(W, H) * 0.85);
    dynamicGrad.addColorStop(0, atmosphereGlowColor);
    dynamicGrad.addColorStop(0.55, "rgba(5, 4, 3, 0.02)");
    dynamicGrad.addColorStop(1, "transparent");
    ctx.fillStyle = dynamicGrad;
    ctx.fillRect(0, 0, W, H);
    particulateArray.forEach((pt) => {
      pt.y -= pt.velocity;
      pt.x += pt.oscRange * 3e-5;
      if (pt.y < 0) pt.y = 1;
      ctx.beginPath();
      ctx.arc(pt.x * W, pt.y * H, pt.size * sc, 0, Math.PI * 2);
      if (p < 0.28) {
        ctx.fillStyle = `rgba(240, 100, 20, ${pt.alpha * 1.8})`;
      } else {
        ctx.fillStyle = `rgba(226, 182, 108, ${pt.alpha * 0.8})`;
      }
      ctx.fill();
    });
  }
  function traceDecanterPath(ctx2, bX, bY, bW, bH, rad) {
    const halfW = bW * 0.45;
    const neckWidth = bW * 0.22;
    const neckHeight = bH * 0.25;
    const crownWidth = neckWidth * 1.25;
    const crownHeight = bH * 0.05;
    const shoulderY = bY - bH / 2 + neckHeight;
    const neckY = bY - bH / 2 + crownHeight;
    const bottomY = bY + bH / 2;
    const baseThickness = bH * 0.08;
    ctx2.moveTo(bX - crownWidth / 2, bY - bH / 2);
    ctx2.lineTo(bX + crownWidth / 2, bY - bH / 2);
    ctx2.lineTo(bX + neckWidth / 2, neckY);
    ctx2.lineTo(bX + neckWidth / 2, shoulderY);
    const maxWWidth = halfW;
    const midShoulderY = shoulderY + bH * 0.12;
    ctx2.lineTo(bX + maxWWidth, midShoulderY);
    const botW = halfW * 0.85;
    ctx2.lineTo(bX + botW, bottomY - rad);
    ctx2.arcTo(bX + botW, bottomY, bX + botW - rad, bottomY, rad);
    const puntBaseWidth = botW * 1.4;
    ctx2.lineTo(bX + puntBaseWidth / 2, bottomY);
    ctx2.lineTo(bX + puntBaseWidth / 6, bottomY - baseThickness);
    ctx2.lineTo(bX - puntBaseWidth / 6, bottomY - baseThickness);
    ctx2.lineTo(bX - puntBaseWidth / 2, bottomY);
    ctx2.lineTo(bX - botW + rad, bottomY);
    ctx2.arcTo(bX - botW, bottomY, bX - botW, bottomY - rad, rad);
    ctx2.lineTo(bX - maxWWidth, midShoulderY);
    ctx2.lineTo(bX - neckWidth / 2, shoulderY);
    ctx2.lineTo(bX - neckWidth / 2, neckY);
    ctx2.lineTo(bX - crownWidth / 2, bY - bH / 2);
    ctx2.closePath();
  }
  function applySpecularLightPass(bX, bY, bW, bH, activeAlpha) {
    if (activeAlpha < 0.01) return;
    ctx.save();
    ctx.globalAlpha = activeAlpha;
    const relativeLX = (mx - CX) / W;
    const relativeLY = (my - CY) / H;
    const refractionShift = relativeLX * bW * 0.45;
    const verticalShift = relativeLY * bH * 0.12;
    ctx.beginPath();
    traceDecanterPath(ctx, bX, bY, bW - 12 * sc, bH - 12 * sc, 14 * sc);
    ctx.clip();
    const specGrad = ctx.createLinearGradient(
      bX - bW / 2 + refractionShift,
      0,
      bX + bW / 3 + refractionShift,
      0
    );
    specGrad.addColorStop(0, "rgba(255, 255, 255, 0)");
    specGrad.addColorStop(0.3, "rgba(255, 255, 255, 0.28)");
    specGrad.addColorStop(0.35, "rgba(255, 255, 255, 0.45)");
    specGrad.addColorStop(0.4, "rgba(255, 255, 255, 0.15)");
    specGrad.addColorStop(0.6, "rgba(255, 255, 255, 0)");
    ctx.fillStyle = specGrad;
    ctx.fillRect(bX - bW, bY - bH, bW * 2, bH * 2);
    ctx.strokeStyle = "rgba(255, 255, 255, 0.18)";
    ctx.lineWidth = 1.5 * sc;
    ctx.beginPath();
    ctx.ellipse(
      bX - bW * 0.35 + refractionShift * 0.1,
      bY + verticalShift,
      bW * 0.06,
      bH * 0.42,
      0,
      Math.PI * 0.5,
      Math.PI * 1.5
    );
    ctx.stroke();
    ctx.restore();
  }
  function drawPhaseFusion(bX, bY, bW, bH, p) {
    const normP = mapRange(p, 0, 0.1, 0, 1);
    ctx.save();
    const crucibleY = bY - bH * 1.2 + normP * bH * 0.4;
    const radGrad = ctx.createRadialGradient(bX, crucibleY + 50 * sc, 10 * sc, bX, crucibleY + 50 * sc, 250 * sc);
    radGrad.addColorStop(0, "#ffffff");
    radGrad.addColorStop(0.15, "#fff49c");
    radGrad.addColorStop(0.5, "#fc5a03");
    radGrad.addColorStop(1, "transparent");
    ctx.fillStyle = radGrad;
    ctx.beginPath();
    ctx.arc(bX, crucibleY + 50 * sc, 250 * sc, 0, Math.PI * 2);
    ctx.fill();
    if (normP > 0.4) {
      const dropNorm = Math.pow(mapRange(normP, 0.4, 1, 0, 1), 3);
      ctx.fillStyle = "#fff49c";
      ctx.shadowColor = "#fc5a03";
      ctx.shadowBlur = 40 * sc;
      const startY = crucibleY + 80 * sc;
      const endY = startY + dropNorm * bH * 1.2;
      const topWidth = 22 * sc;
      const botWidth = 4 * sc + dropNorm * 10 * sc;
      ctx.beginPath();
      ctx.moveTo(bX - topWidth, startY);
      ctx.lineTo(bX + topWidth, startY);
      ctx.bezierCurveTo(bX + topWidth * 0.2, startY + (endY - startY) * 0.5, bX + botWidth * 1.5, endY - botWidth, bX, endY);
      ctx.bezierCurveTo(bX - botWidth * 1.5, endY - botWidth, bX - topWidth * 0.2, startY + (endY - startY) * 0.5, bX - topWidth, startY);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.shadowBlur = 10 * sc;
      ctx.beginPath();
      ctx.moveTo(bX - topWidth * 0.5, startY);
      ctx.lineTo(bX + topWidth * 0.5, startY);
      ctx.bezierCurveTo(bX + topWidth * 0.1, startY + (endY - startY) * 0.5, bX + botWidth * 0.8, endY - botWidth * 1.2, bX, endY - botWidth * 0.2);
      ctx.bezierCurveTo(bX - botWidth * 0.8, endY - botWidth * 1.2, bX - topWidth * 0.1, startY + (endY - startY) * 0.5, bX - topWidth * 0.5, startY);
      ctx.fill();
    }
    ctx.restore();
  }
  function drawPhaseGob(bX, bY, bW, bH, p) {
    const normP = mapRange(p, 0.1, 0.2, 0, 1);
    const gobY = bY - bH * 0.4 + normP * bH * 0.4;
    const gobSize = lerp(45, 80, eOutCubic(normP)) * sc;
    ctx.save();
    ctx.strokeStyle = "rgba(240, 100, 20, 0.15)";
    ctx.lineWidth = 2 * sc;
    ctx.setLineDash([4, 6]);
    ctx.beginPath();
    traceDecanterPath(ctx, bX, bY, bW, bH, 18 * sc);
    ctx.stroke();
    ctx.restore();
    ctx.save();
    const radGrad = ctx.createRadialGradient(bX, gobY, 2, bX, gobY, gobSize);
    radGrad.addColorStop(0, "#ffffff");
    radGrad.addColorStop(0.2, "#fff49c");
    radGrad.addColorStop(0.5, "#fc5a03");
    radGrad.addColorStop(1, "transparent");
    ctx.fillStyle = radGrad;
    ctx.beginPath();
    ctx.arc(bX, gobY, gobSize, 0, Math.PI * 2);
    ctx.fill();
    const nozzleY = bY - bH / 2 - 50 * sc;
    const nozzleH = 40 * sc;
    const nozGrad = ctx.createLinearGradient(bX - 15 * sc, 0, bX + 15 * sc, 0);
    nozGrad.addColorStop(0, "#111");
    nozGrad.addColorStop(0.5, "#444");
    nozGrad.addColorStop(1, "#0a0a0a");
    ctx.fillStyle = nozGrad;
    ctx.beginPath();
    ctx.moveTo(bX - 16 * sc, nozzleY - 20 * sc);
    ctx.lineTo(bX + 16 * sc, nozzleY - 20 * sc);
    ctx.lineTo(bX + 12 * sc, nozzleY + nozzleH);
    ctx.lineTo(bX - 12 * sc, nozzleY + nozzleH);
    ctx.fill();
    ctx.fillStyle = "#fc5a03";
    ctx.shadowColor = "#fc5a03";
    ctx.shadowBlur = 15 * sc;
    ctx.fillRect(bX - 8 * sc, nozzleY + nozzleH - 2 * sc, 16 * sc, 4 * sc);
    ctx.restore();
  }
  function drawPhaseFinalMold(bX, bY, bW, bH, p) {
    const normP = mapRange(p, 0.2, 0.3, 0, 1);
    const moldSeparation = eInOutQuint(mapRange(normP, 0.55, 0.95, 0, 1)) * 180 * sc;
    ctx.save();
    ctx.beginPath();
    traceDecanterPath(ctx, bX, bY, bW, bH, 18 * sc);
    const heatFlicker = Math.sin(frameTimer * 0.12) * 0.08;
    const fusionGrad = ctx.createLinearGradient(bX - bW / 2, 0, bX + bW / 2, 0);
    fusionGrad.addColorStop(0, "#5a1201");
    fusionGrad.addColorStop(0.2, "#cc3b02");
    fusionGrad.addColorStop(0.5, `rgba(255, 180, 50, ${0.92 + heatFlicker})`);
    fusionGrad.addColorStop(0.8, "#cc3b02");
    fusionGrad.addColorStop(1, "#5a1201");
    ctx.fillStyle = fusionGrad;
    ctx.fill();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = "#0e0b09";
    ctx.strokeStyle = "#2b241e";
    ctx.lineWidth = 2.5 * sc;
    const mH = bH * 1.08;
    const mW = bW * 1.15;
    const leftX = bX - mW - moldSeparation - 8 * sc;
    const rightX = bX + moldSeparation + 8 * sc;
    const mY = bY - mH / 2;
    ctx.beginPath();
    ctx.rect(leftX, mY, mW, mH);
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.rect(rightX, mY, mW, mH);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }
  function drawPhaseAnnealing(bX, bY, bW, bH, p) {
    const normP = mapRange(p, 0.3, 0.4, 0, 1);
    const trackSlide = (1 - normP) * 120 * sc;
    const trackingX = bX + trackSlide;
    ctx.save();
    ctx.strokeStyle = "rgba(226, 182, 108, 0.16)";
    ctx.lineWidth = 2.5 * sc;
    ctx.beginPath();
    ctx.moveTo(bX - 300 * sc, bY + bH / 2 + 10 * sc);
    ctx.lineTo(bX + 300 * sc, bY + bH / 2 + 10 * sc);
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.beginPath();
    traceDecanterPath(ctx, trackingX, bY, bW, bH, 18 * sc);
    const coolRate = clamp((1 - normP) * 1.6, 0, 1);
    const hotAura = `rgba(240, 60, 5, ${coolRate * 0.95})`;
    const warmInner = `rgba(255, 180, 20, ${coolRate * 0.82})`;
    const glassBaseClr = `rgba(195, 218, 226, ${0.12 + normP * 0.15})`;
    const glassDeepClr = `rgba(32, 44, 50, ${0.38 + normP * 0.32})`;
    const crystalGrad = ctx.createRadialGradient(
      trackingX,
      bY + bH * 0.15,
      0,
      trackingX,
      bY,
      bH * 0.65
    );
    crystalGrad.addColorStop(0, coolRate > 0.05 ? warmInner : glassBaseClr);
    crystalGrad.addColorStop(0.55, coolRate > 0.05 ? hotAura : glassBaseClr);
    crystalGrad.addColorStop(1, glassDeepClr);
    ctx.fillStyle = crystalGrad;
    ctx.fill();
    ctx.strokeStyle = `rgba(252, 223, 166, ${0.12 + (1 - coolRate) * 0.32})`;
    ctx.lineWidth = 1.2 * sc;
    ctx.stroke();
    applySpecularLightPass(trackingX, bY, bW, bH, normP);
    ctx.restore();
  }
  function drawPhaseInspection(bX, bY, bW, bH, p) {
    const normP = mapRange(p, 0.4, 0.5, 0, 1);
    ctx.save();
    ctx.beginPath();
    traceDecanterPath(ctx, bX, bY, bW, bH, 18 * sc);
    const inspectGrad = ctx.createRadialGradient(bX, bY, 0, bX, bY, bH * 0.7);
    inspectGrad.addColorStop(0, `rgba(${Math.round(lerp(180, 215, normP))}, ${Math.round(lerp(80, 238, normP))}, 245, 0.15)`);
    inspectGrad.addColorStop(0.5, `rgba(${Math.round(lerp(240, 110, normP))}, 132, 142, 0.22)`);
    inspectGrad.addColorStop(1, "rgba(12, 18, 24, 0.78)");
    ctx.fillStyle = inspectGrad;
    ctx.fill();
    ctx.strokeStyle = "rgba(172, 218, 240, 0.38)";
    ctx.lineWidth = 1.3 * sc;
    ctx.stroke();
    applySpecularLightPass(bX, bY, bW, bH, 1);
    ctx.restore();
    const beamY = bY - bH / 2 + normP * bH * 1.05;
    ctx.save();
    ctx.strokeStyle = "#0caccc";
    ctx.lineWidth = 2.5 * sc;
    ctx.shadowColor = "#0caccc";
    ctx.shadowBlur = 15 * sc;
    ctx.beginPath();
    ctx.ellipse(bX, beamY, bW * 0.62, bH * 0.015, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }
  function drawPhaseLaserEtch(bX, bY, bW, bH, p) {
    const normP = mapRange(p, 0.5, 0.6, 0, 1);
    ctx.save();
    ctx.beginPath();
    traceDecanterPath(ctx, bX, bY, bW, bH, 18 * sc);
    const inspectGrad = ctx.createRadialGradient(bX, bY, 0, bX, bY, bH * 0.7);
    inspectGrad.addColorStop(0, "rgba(215, 238, 245, 0.08)");
    inspectGrad.addColorStop(0.68, "rgba(110, 132, 142, 0.15)");
    inspectGrad.addColorStop(1, "rgba(12, 18, 24, 0.75)");
    ctx.fillStyle = inspectGrad;
    ctx.fill();
    ctx.strokeStyle = "rgba(172, 218, 240, 0.38)";
    ctx.lineWidth = 1.3 * sc;
    ctx.stroke();
    applySpecularLightPass(bX, bY, bW, bH, 1);
    ctx.restore();
    const drawTarget = eInOutQuint(normP);
    ctx.save();
    ctx.strokeStyle = `rgba(255, 255, 255, ${Math.min(1, drawTarget * 3 + 0.1)})`;
    ctx.lineWidth = 1 * sc;
    ctx.shadowColor = "#0cffcc";
    ctx.shadowBlur = 10 * sc;
    const crestSize = bW * 0.25;
    ctx.beginPath();
    ctx.arc(bX, bY - bH * 0.05, crestSize, 0, Math.PI * 2 * drawTarget);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(bX - crestSize * 0.6 * drawTarget, bY - bH * 0.05);
    ctx.lineTo(bX + crestSize * 0.6 * drawTarget, bY - bH * 0.05);
    ctx.stroke();
    if (drawTarget > 0.02 && drawTarget < 0.98) {
      const angle = Math.PI * 2 * drawTarget;
      const beamX = bX + Math.cos(angle) * crestSize;
      const beamY = bY - bH * 0.05 + Math.sin(angle) * crestSize;
      ctx.beginPath();
      ctx.moveTo(bX + bW * 2, bY - bH * 1.5);
      ctx.lineTo(beamX, beamY);
      ctx.strokeStyle = `rgba(12, 255, 204, ${0.4 + Math.random() * 0.6})`;
      ctx.lineWidth = clamp(Math.random() * 2, 0.5, 2) * sc;
      ctx.stroke();
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(beamX, beamY, Math.random() * 4 * sc, 0, Math.PI * 2);
      ctx.fill();
      if (soundEngineActive && sfxAudioCtx && frameTimer % 3 === 0) {
        fireChamberImpulse(2400 + Math.random() * 800, 15, 0.05, 0.02);
      }
    }
    ctx.restore();
  }
  function drawPhaseFilling(bX, bY, bW, bH, p) {
    const normP = mapRange(p, 0.6, 0.7, 0, 1);
    const neckH = bH * 0.28;
    const bottomY = bY + bH / 2;
    const liquidMaxH = bH - neckH - 24 * sc;
    const fillPct = mapRange(normP, 0, 0.78, 0, 1);
    const fluidTopY = bottomY - liquidMaxH * fillPct;
    ctx.save();
    ctx.beginPath();
    traceDecanterPath(ctx, bX, bY, bW, bH, 18 * sc);
    const fillingGrad = ctx.createRadialGradient(bX, bY, 0, bX, bY, bH * 0.75);
    fillingGrad.addColorStop(0, "rgba(215, 238, 245, 0.08)");
    fillingGrad.addColorStop(0.68, "rgba(110, 132, 142, 0.15)");
    fillingGrad.addColorStop(1, "rgba(12, 18, 24, 0.75)");
    ctx.fillStyle = fillingGrad;
    ctx.fill();
    ctx.restore();
    if (fillPct > 0.01) {
      ctx.save();
      ctx.beginPath();
      traceDecanterPath(ctx, bX, bY, bW - 14 * sc, bH - 12 * sc, 16 * sc);
      ctx.clip();
      const frequencyFactor = frameTimer * 0.15;
      const waveStrength = fillPct < 0.98 ? 6 * sc * (1 - fillPct) : 0;
      ctx.beginPath();
      ctx.moveTo(bX - bW, bottomY + 15 * sc);
      ctx.lineTo(bX - bW, fluidTopY);
      for (let wx = bX - bW; wx <= bX + bW; wx += 6) {
        const wy = fluidTopY + Math.sin(wx * 0.04 + frequencyFactor) * waveStrength;
        ctx.lineTo(wx, wy);
      }
      ctx.lineTo(bX + bW, bottomY + 15 * sc);
      ctx.closePath();
      const whiskeyGrad = ctx.createLinearGradient(0, fluidTopY, 0, bottomY);
      whiskeyGrad.addColorStop(0, "rgba(240, 155, 30, 0.88)");
      whiskeyGrad.addColorStop(0.38, "rgba(195, 95, 10, 0.95)");
      whiskeyGrad.addColorStop(1, "rgba(92, 32, 2, 0.99)");
      ctx.fillStyle = whiskeyGrad;
      ctx.fill();
      ctx.restore();
    }
    const fillerSpindleY = bY - bH / 2 - 35 * sc + eInOutQuint(mapRange(normP, 0, 0.22, 0, 1)) * 62 * sc - eInOutQuint(mapRange(normP, 0.65, 0.85, 0, 1)) * 62 * sc;
    ctx.save();
    ctx.fillStyle = "#6e3c1a";
    ctx.fillRect(bX - 6 * sc, fillerSpindleY, 12 * sc, 135 * sc);
    if (fillPct > 0.02 && fillPct < 0.99) {
      ctx.save();
      ctx.globalAlpha = 0.88;
      const fluidStreamGrad = ctx.createLinearGradient(bX - 3.5 * sc, 0, bX + 3.5 * sc, 0);
      fluidStreamGrad.addColorStop(0, "rgba(195, 95, 10, 0.9)");
      fluidStreamGrad.addColorStop(0.5, "rgba(252, 223, 166, 0.96)");
      fluidStreamGrad.addColorStop(1, "rgba(195, 95, 10, 0.9)");
      ctx.fillStyle = fluidStreamGrad;
      ctx.fillRect(bX - 3.5 * sc, fillerSpindleY + 135 * sc, 7 * sc, fluidTopY - (fillerSpindleY + 135 * sc));
      ctx.restore();
      if (soundEngineActive && sfxAudioCtx && washFilterGain) {
        const streamVolume = mapRange(p, 0.58, 0.68, 0.05, 0.38);
        washFilterGain.gain.setTargetAtTime(streamVolume, sfxAudioCtx.currentTime, 0.06);
      }
    } else {
      if (soundEngineActive && washFilterGain && sfxAudioCtx) {
        washFilterGain.gain.setTargetAtTime(0, sfxAudioCtx.currentTime, 0.12);
      }
    }
    ctx.restore();
  }
  function drawPhaseCorking(bX, bY, bW, bH, p) {
    const normP = mapRange(p, 0.7, 0.8, 0, 1);
    ctx.save();
    ctx.beginPath();
    traceDecanterPath(ctx, bX, bY, bW, bH, 18 * sc);
    const whiskeyGrad = ctx.createLinearGradient(0, bY - bH / 3, 0, bY + bH / 2);
    whiskeyGrad.addColorStop(0, "rgba(195, 95, 10, 0.95)");
    whiskeyGrad.addColorStop(1, "rgba(92, 32, 2, 0.99)");
    ctx.fillStyle = whiskeyGrad;
    ctx.fill();
    ctx.strokeStyle = "rgba(182, 222, 238, 0.36)";
    ctx.lineWidth = 1.4 * sc;
    ctx.stroke();
    applySpecularLightPass(bX, bY, bW, bH, 1);
    ctx.restore();
    const corkProgress = eInOutQuint(mapRange(normP, 0, 0.8, 0, 1));
    const elasticBounce = eElasticOvershoot(corkProgress) * 50 * sc;
    const initialY = bY - bH / 1.5;
    const corkY = initialY + elasticBounce;
    ctx.save();
    ctx.fillStyle = "#bfa583";
    ctx.strokeStyle = "#765d44";
    ctx.lineWidth = 1 * sc;
    ctx.beginPath();
    drawRoundRect(ctx, bX - bW * 0.11, corkY, bW * 0.22, 30 * sc, 2 * sc);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }
  function drawPhaseCapsule(bX, bY, bW, bH, p) {
    const normP = mapRange(p, 0.8, 0.9, 0, 1);
    ctx.save();
    ctx.beginPath();
    traceDecanterPath(ctx, bX, bY, bW, bH, 18 * sc);
    const whiskeyGrad = ctx.createLinearGradient(0, bY - bH / 3, 0, bY + bH / 2);
    whiskeyGrad.addColorStop(0, "rgba(195, 95, 10, 0.95)");
    whiskeyGrad.addColorStop(1, "rgba(92, 32, 2, 0.99)");
    ctx.fillStyle = whiskeyGrad;
    ctx.fill();
    ctx.strokeStyle = "rgba(182, 222, 238, 0.36)";
    ctx.lineWidth = 1.4 * sc;
    ctx.stroke();
    applySpecularLightPass(bX, bY, bW, bH, 1);
    ctx.restore();
    ctx.save();
    ctx.fillStyle = "#bfa583";
    ctx.strokeStyle = "#765d44";
    ctx.lineWidth = 1 * sc;
    const finalCorkY = bY - bH / 1.5 + Math.max(0, eElasticOvershoot(1) * 50 * sc);
    ctx.beginPath();
    drawRoundRect(ctx, bX - bW * 0.11, finalCorkY, bW * 0.22, 30 * sc, 2 * sc);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    const capsLower = mapRange(normP, 0, 0.3, 0, 1);
    const capsShrink = mapRange(normP, 0.4, 0.9, 0, 1);
    const foilY = bY - bH - 100 * sc + eInOutQuint(capsLower) * (bH / 2 + 100 * sc - 12 * sc);
    const foilWidth = bW * 0.26 + (1 - eInOutQuint(capsShrink)) * 8 * sc;
    ctx.save();
    const goldCapGrad = ctx.createLinearGradient(bX - foilWidth / 2, 0, bX + foilWidth / 2, 0);
    goldCapGrad.addColorStop(0, "#a67c3c");
    goldCapGrad.addColorStop(0.5, "#fcdfa6");
    goldCapGrad.addColorStop(1, "#a67c3c");
    ctx.fillStyle = goldCapGrad;
    ctx.beginPath();
    drawRoundRect(ctx, bX - foilWidth / 2, foilY, foilWidth, 42 * sc, 4 * sc);
    ctx.fill();
    if (capsShrink > 0 && capsShrink < 1) {
      ctx.strokeStyle = `rgba(240, 100, 20, ${Math.sin(capsShrink * Math.PI)})`;
      ctx.lineWidth = 4 * sc;
      ctx.shadowColor = "#fc5a03";
      ctx.shadowBlur = 15 * sc;
      const heatY = foilY + capsShrink * 42 * sc;
      ctx.beginPath();
      ctx.ellipse(bX, heatY, foilWidth * 0.6, 5 * sc, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  }
  function drawFinalBottleBase(bX, bY, bW, bH) {
    ctx.save();
    ctx.beginPath();
    traceDecanterPath(ctx, bX, bY, bW, bH, 18 * sc);
    const deepAgingGrad = ctx.createLinearGradient(0, bY - bH / 2, 0, bY + bH / 2);
    deepAgingGrad.addColorStop(0, "rgba(92, 32, 2, 0.99)");
    deepAgingGrad.addColorStop(1, "rgba(40, 10, 0, 1.0)");
    ctx.fillStyle = deepAgingGrad;
    ctx.fill();
    ctx.strokeStyle = "rgba(255, 255, 255, 0.25)";
    ctx.lineWidth = 1.5 * sc;
    ctx.stroke();
    ctx.beginPath();
    traceDecanterPath(ctx, bX, bY, bW * 0.9, bH * 0.96, 16 * sc);
    ctx.strokeStyle = "rgba(255, 255, 255, 0.08)";
    ctx.lineWidth = 4 * sc;
    ctx.stroke();
    applySpecularLightPass(bX, bY, bW, bH, 1);
    ctx.restore();
    ctx.save();
    const foilWidth = bW * 0.26;
    const foilY = bY - bH / 2 - 12 * sc;
    const goldCapGrad = ctx.createLinearGradient(bX - foilWidth / 2, 0, bX + foilWidth / 2, 0);
    goldCapGrad.addColorStop(0, "#a67c3c");
    goldCapGrad.addColorStop(0.5, "#fcdfa6");
    goldCapGrad.addColorStop(1, "#a67c3c");
    ctx.fillStyle = goldCapGrad;
    ctx.beginPath();
    drawRoundRect(ctx, bX - foilWidth / 2, foilY, foilWidth, 42 * sc, 4 * sc);
    ctx.fill();
    ctx.restore();
  }
  function drawLabel(bX, bY, bW, bH, sealScaleX) {
    if (sealScaleX <= 0.01) return;
    const labelW = bW * 0.94;
    const labelH = labelW / 3.7;
    const labelX = bX - labelW / 2;
    const labelY = bY + bH * 0.04;
    ctx.save();
    ctx.translate(bX, labelY + labelH / 2);
    ctx.scale(sealScaleX, 1);
    ctx.translate(-bX, -(labelY + labelH / 2));
    ctx.shadowColor = "rgba(0, 0, 0, 0.92)";
    ctx.shadowBlur = 12 * sc;
    ctx.shadowOffsetY = 5 * sc;
    ctx.fillStyle = "#fdfbf7";
    ctx.beginPath();
    drawRoundRect(ctx, labelX, labelY, labelW, labelH, 1.5 * sc);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;
    ctx.save();
    ctx.strokeStyle = "#dcd4c4";
    ctx.lineWidth = 0.5 * sc;
    ctx.strokeRect(labelX + 2 * sc, labelY + 2 * sc, labelW - 4 * sc, labelH - 4 * sc);
    ctx.fillStyle = "#5c564d";
    ctx.font = `600 ${3.8 * sc}px 'Montserrat'`;
    ctx.textAlign = "center";
    ctx.letterSpacing = "0.08em";
    ctx.fillText("BARREL SELECTED BY", labelX + labelW * 0.4, labelY + 8 * sc);
    const badgeX = labelX + labelW * 0.12;
    const badgeY = labelY + 11 * sc;
    const badgeW = labelW * 0.58;
    const badgeH = labelH * 0.62;
    ctx.fillStyle = "#1c1a18";
    ctx.fillRect(badgeX, badgeY, badgeW, badgeH);
    ctx.strokeStyle = "#c8923a";
    ctx.lineWidth = 0.5 * sc;
    ctx.strokeRect(badgeX + 1 * sc, badgeY + 1 * sc, badgeW - 2 * sc, badgeH - 2 * sc);
    const barX = badgeX + 8 * sc;
    const barY = badgeY + badgeH / 2;
    const barRadX = 6 * sc;
    const barRadY = 8 * sc;
    ctx.strokeStyle = "#c8923a";
    ctx.lineWidth = 0.8 * sc;
    ctx.beginPath();
    ctx.ellipse(barX, barY, barRadX, barRadY, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(barX - barRadX * 0.9, barY - barRadY * 0.4);
    ctx.lineTo(barX + barRadX * 0.9, barY - barRadY * 0.4);
    ctx.moveTo(barX - barRadX, barY);
    ctx.lineTo(barX + barRadX, barY);
    ctx.moveTo(barX - barRadX * 0.9, barY + barRadY * 0.4);
    ctx.lineTo(barX + barRadX * 0.9, barY + barRadY * 0.4);
    ctx.stroke();
    const hoverRefX = (mx - CX) / W;
    const goldGrad = ctx.createLinearGradient(badgeX, 0, badgeX + badgeW, 0);
    goldGrad.addColorStop(0, "#c8923a");
    goldGrad.addColorStop(clamp(0.4 + hoverRefX * 0.4, 0, 1), "#fcdfa6");
    goldGrad.addColorStop(1, "#a67c3c");
    ctx.fillStyle = goldGrad;
    ctx.textAlign = "left";
    ctx.font = `700 ${8 * sc}px 'Cinzel'`;
    ctx.fillText("MARIA'S", badgeX + 18 * sc, badgeY + 11 * sc);
    ctx.font = `500 ${4.5 * sc}px 'Cinzel'`;
    ctx.fillText("WHISKEY CLUB", badgeX + 18 * sc, badgeY + 17 * sc);
    ctx.save();
    ctx.translate(labelX + labelW * 0.88, labelY + labelH / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillStyle = "#655e53";
    ctx.font = `600 ${3.2 * sc}px 'Montserrat'`;
    ctx.textAlign = "center";
    ctx.fillText("LYNCHBURG, TN", 0, -2 * sc);
    ctx.fillStyle = "#1c1a18";
    ctx.font = `bold ${4.2 * sc}px 'Montserrat'`;
    ctx.fillText("DSP-TN-21001", 0, 3 * sc);
    ctx.restore();
    ctx.restore();
    const waxX = labelX + labelW * 0.83;
    const waxY = labelY + labelH * 0.55;
    const waxRad = 9 * sc;
    ctx.save();
    const waxGrad = ctx.createRadialGradient(
      waxX - waxRad * 0.2,
      waxY - waxRad * 0.2,
      1,
      waxX,
      waxY,
      waxRad
    );
    waxGrad.addColorStop(0, "#bd2d13");
    waxGrad.addColorStop(0.5, "#991802");
    waxGrad.addColorStop(1, "#5a0600");
    ctx.shadowColor = "rgba(0,0,0,0.4)";
    ctx.shadowBlur = 4 * sc;
    ctx.shadowOffsetY = 2 * sc;
    ctx.fillStyle = waxGrad;
    ctx.beginPath();
    ctx.arc(waxX, waxY, waxRad + Math.sin(frameTimer * 0.05) * 0.25 * sc, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    ctx.fillStyle = "rgba(255,255,255,0.45)";
    ctx.font = `600 ${8 * sc}px 'Cinzel'`;
    ctx.textAlign = "center";
    ctx.fillText("M", waxX, waxY + 2.8 * sc);
    ctx.restore();
  }
  function drawPhaseLabelZoom(bX, bY, bW, bH, p) {
    const normP = Math.max(0, mapRange(p, 0.9, 1, 0, 1));
    const zoomFactor = lerp(1, 4.8, eInOutQuint(normP));
    const focusCamY = lerp(bY, bY + bH * 0.15, eInOutQuint(normP));
    ctx.save();
    ctx.translate(CX, CY);
    ctx.scale(zoomFactor, zoomFactor);
    ctx.translate(-bX, -focusCamY);
    drawFinalBottleBase(bX, bY, bW, bH);
    const wrappingP = clamp(mapRange(normP, 0, 0.48, 0, 1), 0, 1);
    drawLabel(bX, bY, bW, bH, eOutCubic(wrappingP));
    ctx.restore();
  }
  function drawBlueprintBottle(p, bX, bY, bW, bH) {
    ctx.save();
    ctx.fillStyle = "rgba(2, 6, 14, 0.8)";
    ctx.fillRect(0, 0, W, H);
    const gridP = mapRange(p, 0, 0.33, 0, 1);
    if (gridP > 0) {
      ctx.strokeStyle = `rgba(12, 140, 204, ${gridP * 0.15})`;
      ctx.lineWidth = 1;
      const gridSize = 40 * sc;
      for (let x = W / 2 % gridSize; x < W; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, H);
        ctx.stroke();
      }
      for (let y = H / 2 % gridSize; y < H; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
        ctx.stroke();
      }
    }
    const vecP = Math.min(1, mapRange(p, 0.2, 0.66, 0, 1));
    if (vecP > 0) {
      ctx.strokeStyle = `rgba(12, 255, 204, ${vecP * 0.8})`;
      ctx.lineWidth = 1.5 * sc;
      if (vecP < 0.99) {
        ctx.setLineDash([5, 10]);
        ctx.lineDashOffset = -frameTimer * 1.5;
      }
      ctx.beginPath();
      traceDecanterPath(ctx, bX, bY, bW, bH, 18 * sc);
      ctx.stroke();
      ctx.setLineDash([]);
      const points = [
        [bX, bY - bH / 2],
        [bX - bW / 2, bY],
        [bX + bW / 2, bY],
        [bX, bY + bH / 2]
      ];
      ctx.fillStyle = "#0cffcc";
      points.forEach((pt) => {
        ctx.beginPath();
        ctx.arc(pt[0], pt[1], 3 * sc * vecP, 0, Math.PI * 2);
        ctx.fill();
      });
    }
    const metP = Math.min(1, mapRange(p, 0.6, 1, 0, 1));
    if (metP > 0) {
      ctx.globalAlpha = metP;
      ctx.fillStyle = "#ffb400";
      ctx.font = `${8 * sc}px "JetBrains Mono", monospace`;
      ctx.beginPath();
      ctx.moveTo(bX - bW * 0.14, bY - bH / 2 + 20 * sc);
      ctx.lineTo(bX + bW * 0.14, bY - bH / 2 + 20 * sc);
      ctx.strokeStyle = "#ffb400";
      ctx.stroke();
      ctx.fillText("\xD8 28.5mm", bX + bW * 0.2, bY - bH / 2 + 22 * sc);
      ctx.beginPath();
      ctx.moveTo(bX - bW / 2, bY + bH / 2 + 30 * sc);
      ctx.lineTo(bX + bW / 2, bY + bH / 2 + 30 * sc);
      ctx.stroke();
      ctx.fillText("WIDTH 168mm", bX, bY + bH / 2 + 45 * sc);
      ctx.globalAlpha = 1;
    }
    ctx.restore();
  }
  function drawAuraNode(text, xPos, yBase, phase, color, alpha, bX) {
    if (alpha <= 0) return;
    ctx.save();
    ctx.globalAlpha = alpha;
    const floatY = yBase - Math.sin(frameTimer * 0.03 + phase) * 15 * sc;
    const floatX = xPos + Math.cos(frameTimer * 0.02 + phase) * 15 * sc;
    ctx.shadowColor = color;
    ctx.shadowBlur = 20 * sc;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(floatX, floatY, 5 * sc, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(bX, yBase);
    ctx.lineTo(floatX, floatY);
    ctx.strokeStyle = `rgba(255,255,255,0.1)`;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.fillStyle = "#fff";
    ctx.font = `300 ${9 * sc}px var(--sans)`;
    ctx.textAlign = floatX > bX ? "left" : "right";
    ctx.fillText(text, floatX + (floatX > bX ? 15 * sc : -15 * sc), floatY + 3 * sc);
    ctx.restore();
  }
  function drawTastingBottle(p, bX, bY, bW, bH) {
    drawFinalBottleBase(bX, bY, bW, bH);
    drawLabel(bX, bY, bW, bH, 1);
    const noseAlpha = Math.min(mapRange(p, 0, 0.1, 0, 1), 1 - mapRange(p, 0.4, 0.5, 0, 1));
    drawAuraNode(ACTIVE_NOTES.nose[0], bX + bW * 0.9, bY - bH * 0.4, 0, "#fcdfa6", noseAlpha, bX);
    drawAuraNode(ACTIVE_NOTES.nose[1], bX - bW * 0.8, bY - bH * 0.5, 2, "#a67c3c", noseAlpha, bX);
    const palateAlpha = Math.min(mapRange(p, 0.3, 0.4, 0, 1), 1 - mapRange(p, 0.7, 0.8, 0, 1));
    drawAuraNode(ACTIVE_NOTES.palate[0], bX + bW * 1, bY, 1, "#991802", palateAlpha, bX);
    drawAuraNode(ACTIVE_NOTES.palate[1], bX - bW * 0.9, bY + bH * 0.1, 3, "#e2b66c", palateAlpha, bX);
    const finishAlpha = mapRange(p, 0.6, 0.7, 0, 1);
    drawAuraNode(ACTIVE_NOTES.finish[0], bX + bW * 0.85, bY + bH * 0.4, 4, "#7e6a51", finishAlpha, bX);
    drawAuraNode(ACTIVE_NOTES.finish[1], bX - bW * 0.8, bY + bH * 0.3, 5, "#6e3c1a", finishAlpha, bX);
  }
  function drawVaultBottle(p, bX, bY, bW, bH) {
    ctx.fillStyle = "#020202";
    ctx.fillRect(0, 0, W, H);
    ctx.save();
    ctx.strokeStyle = "rgba(223, 179, 107, 0.04)";
    ctx.lineWidth = 1 * sc;
    const perspectiveScale = 1 + p * 2;
    ctx.translate(bX, bY + bH / 2);
    ctx.scale(perspectiveScale, perspectiveScale);
    const gridSpacing = 40 * sc;
    for (let i = 1; i < 20; i++) {
      const rad = i * gridSpacing;
      ctx.beginPath();
      for (let j = 0; j < 6; j++) {
        const angle = j * Math.PI / 3;
        const px = Math.cos(angle) * rad;
        const py = Math.sin(angle) * rad * 0.3;
        if (j === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.stroke();
    }
    for (let j = 0; j < 6; j++) {
      const angle = j * Math.PI / 3;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(Math.cos(angle) * 800 * sc, Math.sin(angle) * 800 * sc * 0.3);
      ctx.stroke();
    }
    ctx.translate(-bX, -(bY + bH / 2));
    if (p > 0.1 && p < 0.9) {
      let scanNorm = mapRange(p, 0.1, 0.9, 0, 1);
      const scanY = bY - bH * 0.8 + scanNorm * bH * 1.6;
      ctx.shadowColor = "#dfb36b";
      ctx.shadowBlur = 15 * sc;
      ctx.fillStyle = "rgba(223, 179, 107, 0.4)";
      ctx.fillRect(bX - bW * 1.5, scanY, bW * 3, 2 * sc);
      const scanGrad = ctx.createLinearGradient(0, scanY, 0, scanY - 60 * sc);
      scanGrad.addColorStop(0, "rgba(223, 179, 107, 0.15)");
      scanGrad.addColorStop(1, "transparent");
      ctx.fillStyle = scanGrad;
      ctx.beginPath();
      ctx.moveTo(bX - bW * 1.5, scanY);
      ctx.lineTo(bX + bW * 1.5, scanY);
      ctx.lineTo(bX + bW * 0.5, scanY - 60 * sc);
      ctx.lineTo(bX - bW * 0.5, scanY - 60 * sc);
      ctx.fill();
      ctx.shadowBlur = 0;
    }
    ctx.restore();
    ctx.save();
    ctx.globalCompositeOperation = "screen";
    const pedBaseGrad = ctx.createRadialGradient(bX, bY + bH / 2, 10 * sc, bX, bY + bH / 2, bW * 1.2);
    pedBaseGrad.addColorStop(0, `rgba(223, 179, 107, ${0.4 * (1 - p)})`);
    pedBaseGrad.addColorStop(1, "transparent");
    ctx.fillStyle = pedBaseGrad;
    ctx.beginPath();
    ctx.ellipse(bX, bY + bH / 2, bW * 0.8, bW * 0.2, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = "source-over";
    ctx.save();
    ctx.translate(bX, bY + bH / 2);
    ctx.scale(1, -0.3);
    ctx.translate(-bX, -(bY + bH / 2));
    ctx.globalAlpha = 0.3 * (1 - p);
    drawFinalBottleBase(bX, bY, bW, bH);
    drawLabel(bX, bY, bW, bH, 1);
    ctx.restore();
    ctx.globalAlpha = 1;
    drawFinalBottleBase(bX, bY, bW, bH);
    drawLabel(bX, bY, bW, bH, 1);
    ctx.save();
    ctx.globalAlpha = (Math.sin(frameTimer * 0.1) * 0.5 + 0.5) * (1 - p);
    ctx.fillStyle = "rgba(223, 179, 107, 0.8)";
    ctx.font = `500 ${8 * sc}px var(--mono)`;
    ctx.textAlign = "right";
    ctx.fillText(`CASK: #4802`, bX - bW * 0.8, bY - bH * 0.2);
    ctx.fillText(`LOCATION: SECTOR 4`, bX - bW * 0.8, bY - bH * 0.2 + 12 * sc);
    ctx.fillText(`STATUS: SECURE`, bX - bW * 0.8, bY - bH * 0.2 + 24 * sc);
    ctx.textAlign = "left";
    ctx.fillText(`TEMP: 14.5\xB0C`, bX + bW * 0.8, bY + bH * 0.2);
    ctx.fillText(`HUMIDITY: 68%`, bX + bW * 0.8, bY + bH * 0.2 + 12 * sc);
    ctx.restore();
    ctx.restore();
  }
  function drawTerroirBottle(p, bX, bY, bW, bH) {
    let baseGrad = "#12120e";
    if (p < 0.33) baseGrad = "rgba(74, 60, 31, 0.4)";
    else if (p < 0.66) baseGrad = "rgba(28, 55, 71, 0.4)";
    else baseGrad = "rgba(64, 25, 10, 0.4)";
    ctx.fillStyle = baseGrad;
    ctx.fillRect(0, 0, W, H);
    ctx.globalAlpha = 0.6;
    drawFinalBottleBase(bX, bY, bW, bH);
    ctx.globalAlpha = 1;
    const barleyAlpha = Math.min(mapRange(p, 0, 0.1, 0, 1), 1 - mapRange(p, 0.25, 0.33, 0, 1));
    drawAuraNode("ESTATE BARLEY", bX - bW * 0.9, bY, 0, "#e5c07b", barleyAlpha, bX);
    const waterAlpha = Math.min(mapRange(p, 0.33, 0.4, 0, 1), 1 - mapRange(p, 0.55, 0.66, 0, 1));
    drawAuraNode("LIMESTONE AQUIFER", bX + bW * 0.9, bY + bH * 0.1, 2, "#61afef", waterAlpha, bX);
    const oakAlpha = Math.min(mapRange(p, 0.66, 0.75, 0, 1), 1 - mapRange(p, 0.9, 1, 0, 1));
    drawAuraNode("CRAFT COOPERAGE", bX - bW * 0.8, bY + bH * 0.3, 4, "#d19a66", oakAlpha, bX);
    ctx.save();
    for (let i = 0; i < 30; i++) {
      const floatY = (H + frameTimer * (0.5 + i % 3) * sc + i * 40 * sc) % H;
      const floatX = W / 2 + Math.sin(frameTimer * 0.01 + i) * (W / 3);
      ctx.fillStyle = `rgba(255,255,255,${0.1 + i % 3 * 0.05})`;
      ctx.beginPath();
      ctx.arc(floatX, H - floatY, 1 * sc + i % 2 * sc, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
  function drawJourneyBottle(p, bX, bY, bW, bH) {
    ctx.fillStyle = "#0a0806";
    ctx.fillRect(0, 0, W, H);
    const smoothAlpha = (val, startFadeIn, fullIn, startFadeOut, fullOut) => {
      if (val <= startFadeIn || val >= fullOut) return 0;
      if (val >= fullIn && val <= startFadeOut) return 1;
      if (val > startFadeIn && val < fullIn) return (val - startFadeIn) / (fullIn - startFadeIn);
      return 1 - (val - startFadeOut) / (fullOut - startFadeOut);
    };
    const aField = smoothAlpha(p, -0.1, 0, 0.2, 0.3);
    const aDistill = smoothAlpha(p, 0.2, 0.3, 0.45, 0.55);
    const aBottle = smoothAlpha(p, 0.45, 0.55, 0.65, 0.75);
    const aTransit = smoothAlpha(p, 0.65, 0.7, 0.8, 0.85);
    const aHero = smoothAlpha(p, 0.8, 0.85, 1.1, 1.2);
    if (aField > 0) {
      ctx.save();
      ctx.globalAlpha = aField;
      const fieldGrad = ctx.createLinearGradient(0, 0, 0, H);
      fieldGrad.addColorStop(0, "#2e1c0c");
      fieldGrad.addColorStop(1, "#a67c3c");
      ctx.fillStyle = fieldGrad;
      ctx.fillRect(0, 0, W, H);
      ctx.globalCompositeOperation = "screen";
      const panX = -p * W * 1.5;
      ctx.strokeStyle = "rgba(255, 230, 150, 0.15)";
      ctx.lineWidth = 1 * sc;
      ctx.beginPath();
      for (let i = 0; i < 60; i++) {
        const lx = (panX + i * 40 * sc) % W;
        const px = lx < 0 ? lx + W : lx;
        ctx.moveTo(px, H);
        ctx.quadraticCurveTo(px + Math.sin(frameTimer * 0.02 + i) * 30 * sc, H * 0.6, px + 50 * sc, H * 0.3 + i % 5 * 20 * sc);
      }
      ctx.stroke();
      const sunGrad = ctx.createRadialGradient(W * 0.5, H * 0.3, 0, W * 0.5, H * 0.3, W * 0.6);
      sunGrad.addColorStop(0, "rgba(255, 200, 100, 0.3)");
      sunGrad.addColorStop(1, "transparent");
      ctx.fillStyle = sunGrad;
      ctx.fillRect(0, 0, W, H);
      ctx.restore();
    }
    if (aDistill > 0) {
      ctx.save();
      ctx.globalAlpha = aDistill;
      const copperBg = ctx.createLinearGradient(0, 0, 0, H);
      copperBg.addColorStop(0, "#140c06");
      copperBg.addColorStop(1, "#4a2b16");
      ctx.fillStyle = copperBg;
      ctx.fillRect(0, 0, W, H);
      ctx.globalCompositeOperation = "screen";
      const shiftX = (p - 0.2) * W * 0.8;
      ctx.fillStyle = "rgba(223, 179, 107, 0.03)";
      ctx.fillRect(W * 0.25 - shiftX, 0, 60 * sc, H);
      ctx.fillRect(W * 0.75 - shiftX, 0, 140 * sc, H);
      for (let i = 0; i < 6; i++) {
        const cx = W * 0.2 + i * 160 * sc - shiftX * 1.2;
        const cr = ctx.createLinearGradient(cx, 0, cx + 80 * sc, 0);
        cr.addColorStop(0, "rgba(223,179,107,0)");
        cr.addColorStop(0.5, "rgba(223,179,107,0.08)");
        cr.addColorStop(1, "rgba(223,179,107,0)");
        ctx.fillStyle = cr;
        ctx.beginPath();
        ctx.moveTo(cx, 0);
        ctx.lineTo(cx + 80 * sc, 0);
        ctx.lineTo(cx + 60 * sc, H);
        ctx.lineTo(cx - 20 * sc, H);
        ctx.fill();
      }
      ctx.restore();
    }
    if (aBottle > 0) {
      ctx.save();
      ctx.globalAlpha = aBottle;
      const botBg = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, W * 0.8);
      botBg.addColorStop(0, "#261b12");
      botBg.addColorStop(1, "#0a0806");
      ctx.fillStyle = botBg;
      ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = "rgba(255, 255, 255, 0.02)";
      ctx.lineWidth = 1 * sc;
      ctx.beginPath();
      for (let i = 0; i < 12; i++) {
        ctx.moveTo(0, H * 0.15 + i * 40 * sc);
        ctx.lineTo(W, H * 0.15 + i * 40 * sc);
      }
      ctx.stroke();
      ctx.restore();
    }
    if (aTransit > 0) {
      ctx.save();
      ctx.globalAlpha = aTransit;
      const transBg = ctx.createLinearGradient(0, 0, 0, H);
      transBg.addColorStop(0, "#0a0806");
      transBg.addColorStop(1, "#1c130b");
      ctx.fillStyle = transBg;
      ctx.fillRect(0, 0, W, H);
      ctx.globalCompositeOperation = "screen";
      for (let i = 0; i < 25; i++) {
        const sy = H * 0.1 + i * 30 * sc;
        const sx = (frameTimer * (20 + i % 5) * sc + i * 100) % (W * 2) - W;
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(sx + 400 * sc, sy);
        ctx.strokeStyle = `rgba(223, 179, 107, ${0.05 + i % 3 * 0.08})`;
        ctx.lineWidth = 1 * sc + i % 2 * 2 * sc;
        ctx.stroke();
      }
      ctx.restore();
    }
    if (aHero > 0) {
      ctx.save();
      ctx.globalAlpha = aHero;
      const heroBg = ctx.createRadialGradient(bX, bY, 0, bX, bY, W * 0.7);
      heroBg.addColorStop(0, "#362211");
      heroBg.addColorStop(0.6, "#0f0a06");
      heroBg.addColorStop(1, "#000000");
      ctx.fillStyle = heroBg;
      ctx.fillRect(0, 0, W, H);
      ctx.restore();
    }
    ctx.save();
    const mStartX = W * 0.15;
    const mEndX = W * 0.85;
    const mY = H * 0.92;
    ctx.globalAlpha = 1 - aHero;
    if (ctx.globalAlpha > 0) {
      ctx.beginPath();
      ctx.moveTo(mStartX, mY);
      ctx.lineTo(mEndX, mY);
      ctx.strokeStyle = "rgba(223, 179, 107, 0.15)";
      ctx.lineWidth = 1 * sc;
      ctx.stroke();
      const currentMapX = lerp(mStartX, mEndX, p);
      ctx.beginPath();
      ctx.moveTo(mStartX, mY);
      ctx.lineTo(currentMapX, mY);
      ctx.strokeStyle = "#dfb36b";
      ctx.lineWidth = 1.5 * sc;
      ctx.stroke();
      const drawRouteNode = (nx, title, isPast) => {
        ctx.fillStyle = isPast ? "#dfb36b" : "#222";
        ctx.beginPath();
        ctx.arc(nx, mY, 2.5 * sc, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = isPast ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.2)";
        ctx.font = `400 ${Math.max(9, 8 * sc)}px var(--sans)`;
        ctx.textAlign = "center";
        ctx.fillText(title, nx, mY + 22 * sc);
      };
      drawRouteNode(mStartX, "WHEAT", p >= 0);
      drawRouteNode(lerp(mStartX, mEndX, 0.25), "DISTILL", p >= 0.2);
      drawRouteNode(lerp(mStartX, mEndX, 0.5), "BOTTLE", p >= 0.4);
      drawRouteNode(lerp(mStartX, mEndX, 0.75), "TRANSIT", p >= 0.6);
      drawRouteNode(mEndX, "ARRIVAL", p >= 0.8);
    }
    ctx.restore();
    ctx.save();
    const easeInOutCubic = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const baseScale = 0.8 + clamp(p, 0, 0.8) * 0.1;
    let heroScale = baseScale;
    if (p > 0.8) {
      const ep = easeInOutCubic(mapRange(p, 0.8, 1, 0, 1));
      heroScale = baseScale + ep * 1.5;
    }
    ctx.translate(bX, bY);
    ctx.scale(heroScale, heroScale);
    ctx.translate(-bX, -bY);
    ctx.fillStyle = "rgba(10, 8, 6, 0.9)";
    ctx.strokeStyle = "rgba(255, 255, 255, 0.1)";
    ctx.lineWidth = 1 * sc;
    ctx.beginPath();
    ctx.ellipse(bX, bY + bH * 0.5 + 4 * sc, bW * 1.2, 10 * sc, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    const bottleFormationAlpha = clamp(mapRange(p, 0, 0.2, 0, 1), 0, 1);
    ctx.globalAlpha = bottleFormationAlpha;
    if (bottleFormationAlpha > 0) {
      drawFinalBottleBase(bX, bY, bW, bH);
      const liquidReveal = clamp(mapRange(p, 0.25, 0.4, 0, 1), 0, 1);
      if (liquidReveal > 0) {
        ctx.save();
        ctx.beginPath();
        traceDecanterPath(ctx, bX, bY, bW * 0.9, bH * 0.9, 16 * sc);
        ctx.clip();
        const fillY = bY + bH / 2 - liquidReveal * bH * 0.7;
        const liqGrad = ctx.createLinearGradient(0, fillY, 0, bY + bH / 2);
        liqGrad.addColorStop(0, "rgba(235, 160, 50, 0.8)");
        liqGrad.addColorStop(1, "rgba(120, 40, 0, 0.9)");
        ctx.fillStyle = liqGrad;
        ctx.fillRect(bX - bW, fillY, bW * 2, bH);
        ctx.restore();
      }
      const labelReveal = clamp(mapRange(p, 0.45, 0.55, 0, 1), 0, 1);
      if (labelReveal > 0) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(bX - bW, bY - bH, bW * 2, bH * 2 * labelReveal);
        ctx.clip();
        drawLabel(bX, bY, bW, bH, 1);
        ctx.restore();
      }
      if (aHero > 0) {
        ctx.save();
        ctx.globalAlpha = aHero * 0.6;
        ctx.globalCompositeOperation = "screen";
        const glint = ctx.createLinearGradient(bX - bW * 0.2, bY - bH / 2, bX - bW * 0.1, bY + bH / 2);
        glint.addColorStop(0, "rgba(255,255,255,0)");
        glint.addColorStop(0.5, "rgba(255,255,255,0.7)");
        glint.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = glint;
        ctx.beginPath();
        traceDecanterPath(ctx, bX, bY, bW, bH, 18 * sc);
        ctx.fill();
        const edgeHighlight = ctx.createLinearGradient(bX + bW * 0.3, bY - bH / 2, bX + bW * 0.4, bY - bH / 4);
        edgeHighlight.addColorStop(0, "rgba(223,179,107,0)");
        edgeHighlight.addColorStop(0.5, "rgba(223,179,107,0.5)");
        edgeHighlight.addColorStop(1, "rgba(223,179,107,0)");
        ctx.fillStyle = edgeHighlight;
        ctx.fill();
        ctx.restore();
      }
    }
    ctx.restore();
  }
  const MODES_CONTENT = {
    default: {
      chapters: [
        { roman: "I", name: "Gob Feed", limit: [0, 0.1] },
        { roman: "II", name: "Parison Blow", limit: [0.1, 0.2] },
        { roman: "III", name: "Final Blow Mold", limit: [0.2, 0.3] },
        { roman: "IV", name: "Thermal Annealing", limit: [0.3, 0.4] },
        { roman: "V", name: "Polarized Inspection", limit: [0.4, 0.5] },
        { roman: "VI", name: "Laser Etching", limit: [0.5, 0.6] },
        { roman: "VII", name: "Spirit Dispensation", limit: [0.6, 0.7] },
        { roman: "VIII", name: "Cork Press", limit: [0.7, 0.8] },
        { roman: "IX", name: "Capsule Lock", limit: [0.8, 0.9] },
        { roman: "X", name: "Club Selection Tag", limit: [0.9, 1] }
      ],
      scribes: [
        { headline: "Gob Feed", desc: "White-hot molten glass drops directly from the feeder into the rough blank mold.", s: 0, e: 0.1 },
        { headline: "Parison Blow", desc: "Initial pneumatic blow forms the parison shape before transferring to the final mold.", s: 0.1, e: 0.2 },
        { headline: "Final Blow Mold", desc: "Heavy cast-iron mold halves lock shut around the parison, forming the distinct contours.", s: 0.2, e: 0.3 },
        { headline: "Thermal Annealing", desc: "Conveyors transport the bottle through the Lehr tunnel, cooling gradually.", s: 0.3, e: 0.4 },
        { headline: "Polarized Sweep", desc: "A polarized scan identifies micro-fissures and ensures perfect molecular alignment.", s: 0.4, e: 0.5 },
        { headline: "Laser Etching", desc: "High-precision laser inscribes the foundation crest onto the cooled glass surface.", s: 0.5, e: 0.6 },
        { headline: "Spirit Dispensation", desc: "Rich amber-aged single malt whiskey flows down the decanter wall rapidly.", s: 0.6, e: 0.7 },
        { headline: "Cork Pressing", desc: "A natural oak cork is pressed fiercely into the decanter neck.", s: 0.7, e: 0.8 },
        { headline: "Capsule Lock", desc: "A thermal head shrink-wraps a metallic gold capsule around the lip.", s: 0.8, e: 0.9 },
        { headline: "Club Selection Tag", desc: "Final application of the signature ribbon label for Maria\u2019s Whiskey Club.", s: 0.9, e: 1 }
      ]
    },
    blueprint: {
      chapters: [
        { roman: "I", name: "Iso-Grid Setup", limit: [0, 0.33] },
        { roman: "II", name: "Vector Profiling", limit: [0.33, 0.66] },
        { roman: "III", name: "Metrology Specs", limit: [0.66, 1] }
      ],
      scribes: [
        { headline: "Geometric Foundation", desc: "Establishing spatial coordinates and symmetry constraints for the structural glass body.", s: 0, e: 0.33 },
        { headline: "Vector Path Tracing", desc: "Defining precise bezier contours for the crystal shoulders, neck, and heavyweight base.", s: 0.33, e: 0.66 },
        { headline: "Metrology Annotations", desc: "Calculating volumetric capacities, wall thicknesses, and center-of-mass tolerances.", s: 0.66, e: 1 }
      ]
    },
    tasting: {
      chapters: [
        { roman: "I", name: "The Nose", limit: [0, 0.33] },
        { roman: "II", name: "The Palate", limit: [0.33, 0.66] },
        { roman: "III", name: "The Finish", limit: [0.66, 1] }
      ],
      scribes: [
        { headline: "Aromatic Bouquet", desc: "Delicate notes of toasted Madagascar vanilla and charred oak drift upward upon opening.", s: 0, e: 0.33 },
        { headline: "Rich Decadence", desc: "A velvety rush of dark cherry, salted caramel, and toasted marzipan coats the palate.", s: 0.33, e: 0.66 },
        { headline: "Enduring Warmth", desc: "Lingering echoes of mild peat smoke, old leather, and a whisper of dark cocoa powder.", s: 0.66, e: 1 }
      ]
    },
    vault: {
      chapters: [
        { roman: "I", name: "Dormant Reserve", limit: [0, 0.5] },
        { roman: "II", name: "Spotlight Reveal", limit: [0.5, 1] }
      ],
      scribes: [
        { headline: "The Quiet Aging", desc: "Resting patiently in the founder\u2019s private vault, sheltered in pure darkness.", s: 0, e: 0.5 },
        { headline: "The Final Masterpiece", desc: "Illuminated presentation of the master blend, showcasing impeccable clarity and rich amber hues.", s: 0.5, e: 1 }
      ]
    },
    terroir: {
      chapters: [
        { roman: "I", name: "Estate Barley", limit: [0, 0.33] },
        { roman: "II", name: "Limestone Water", limit: [0.33, 0.66] },
        { roman: "III", name: "Charred Oak", limit: [0.66, 1] }
      ],
      scribes: [
        { headline: "Single Estate Grain", desc: "Harvested from private fields in late summer, rich in starches and robust flavor potential.", s: 0, e: 0.33 },
        { headline: "Deep Aquifer Source", desc: "Pure limestone-filtered water drawn from ancient natural springs beneath the distillery.", s: 0.33, e: 0.66 },
        { headline: "Custom Cooperage", desc: "Air-dried American white oak, toasted and heavily charred to unlock deep vanilla and caramel.", s: 0.66, e: 1 }
      ]
    },
    journey: {
      chapters: [
        { roman: "I", name: "Golden Harvest", limit: [0, 0.2] },
        { roman: "II", name: "Modern Distilling", limit: [0.2, 0.4] },
        { roman: "III", name: "The Bottling", limit: [0.4, 0.6] },
        { roman: "IV", name: "The Journey North", limit: [0.6, 0.8] },
        { roman: "V", name: "Premium Finish", limit: [0.8, 1] }
      ],
      scribes: [
        { headline: "The Wheat Field", desc: "Starting in a beautiful, sun-drenched wheat field, the grain is harvested in warm sunlight.", s: 0, e: 0.2 },
        { headline: "Copper & Steel", desc: "Processed in a clean, modern distillery using premium copper stills and rigorous craftsmanship.", s: 0.2, e: 0.4 },
        { headline: "Precision Packaging", desc: "The refined spirit is carefully bottled, sealed, and neatly labeled to perfection.", s: 0.4, e: 0.6 },
        { headline: "Tennessee to Michigan", desc: "A smooth transit via highways and state lines, carrying the bottles to their final destination.", s: 0.6, e: 0.8 },
        { headline: "The Decanter", desc: "A dramatic close-up on the premium glass finish, rich texture, and elegant label detail.", s: 0.8, e: 1 }
      ]
    }
  };
  let cachedChapterRoman = "I";
  function animateInteractiveUIPanels(p) {
    elProgressBar.style.width = p * 100 + "%";
    elMetricPct.textContent = (p * 100).toFixed(1) + "%";
    elSlider.value = (p * 1e3).toString();
    const currentMode = currentViewMode;
    const contentKey = currentMode === "blueprint" || currentMode === "tasting" || currentMode === "vault" || currentMode === "terroir" || currentMode === "journey" ? currentMode : "default";
    const chaps = MODES_CONTENT[contentKey].chapters;
    const scribes = MODES_CONTENT[contentKey].scribes;
    const chapObj = chaps.find((c) => p >= c.limit[0] && p <= c.limit[1]) || chaps[chaps.length - 1];
    if (chapObj.roman !== cachedChapterRoman) {
      fireChamberImpulse(900, 4.5, 0.38, 0.08);
      cachedChapterRoman = chapObj.roman;
    }
    elChapterRoman.textContent = chapObj.roman;
    elChapterName.textContent = chapObj.name;
    const story = scribes.find((s) => p >= s.s && p <= s.e);
    if (story && story.headline) {
      elCapHeadline.textContent = story.headline;
      elCapText.textContent = story.desc;
      const fadeEntrance = mapRange(p, story.s, story.s + 0.02, 0, 1);
      const fadeExit = mapRange(p, story.e - 0.02, story.e, 0, 1);
      const resolvedAlpha = Math.min(eOutCubic(fadeEntrance), 1 - eInOutQuint(fadeExit));
      elCapHeadline.style.opacity = resolvedAlpha.toString();
      elCapText.style.opacity = resolvedAlpha.toString();
    } else {
      elCapHeadline.style.opacity = "0";
      elCapText.style.opacity = "0";
    }
    elScrollCue.style.opacity = p < 0.03 ? "1" : "0";
  }
  let frameTimer = 0;
  function drawSingleBottleStage(phase_p, b_X, bY, botW, botH) {
    if (phase_p < 0.1) {
      drawPhaseFusion(b_X, bY, botW, botH, Math.max(0, phase_p));
    } else if (phase_p < 0.2) {
      drawPhaseGob(b_X, bY, botW, botH, phase_p);
    } else if (phase_p < 0.3) {
      drawPhaseFinalMold(b_X, bY, botW, botH, phase_p);
    } else if (phase_p < 0.4) {
      drawPhaseAnnealing(b_X, bY, botW, botH, phase_p);
    } else if (phase_p < 0.5) {
      drawPhaseInspection(b_X, bY, botW, botH, phase_p);
    } else if (phase_p < 0.6) {
      drawPhaseLaserEtch(b_X, bY, botW, botH, phase_p);
    } else if (phase_p < 0.7) {
      drawPhaseFilling(b_X, bY, botW, botH, phase_p);
    } else if (phase_p < 0.8) {
      drawPhaseCorking(b_X, bY, botW, botH, phase_p);
    } else if (phase_p < 0.9) {
      drawPhaseCapsule(b_X, bY, botW, botH, phase_p);
    } else {
      drawPhaseLabelZoom(b_X, bY, botW, botH, Math.min(1, phase_p));
    }
  }
  function renderChronicle(p) {
    frameTimer++;
    drawBackgroundAtmosphere(p);
    const botW = 168 * sc;
    const botH = 345 * sc;
    const bY = CY + 30 * sc;
    const mode = currentViewMode;
    if (mode === "blueprint") {
      drawBlueprintBottle(p, CX, bY, botW, botH);
    } else if (mode === "tasting") {
      drawTastingBottle(p, CX, bY, botW, botH);
    } else if (mode === "vault") {
      drawVaultBottle(p, CX, bY, botW, botH);
    } else if (mode === "terroir") {
      drawTerroirBottle(p, CX, bY, botW, botH);
    } else if (mode === "journey") {
      drawJourneyBottle(p, CX, bY, botW, botH);
    } else if (mode === "chronicle") {
      drawSingleBottleStage(p, CX, bY, botW, botH);
    } else {
      const travel_dist = W * 1.5;
      const start_X = CX - travel_dist / 2;
      const screen_spacing = botW * 2.8;
      const p_spacing = screen_spacing / travel_dist;
      const bottlesToDraw = 25;
      const beltOffset = p * travel_dist % (40 * sc);
      ctx.save();
      ctx.fillStyle = "#080605";
      ctx.fillRect(0, bY + botH / 2 - 2 * sc, W, 22 * sc);
      ctx.strokeStyle = "rgba(226, 182, 108, 0.25)";
      ctx.lineWidth = 1.2 * sc;
      for (let lx = -beltOffset - 40 * sc; lx < W + 80 * sc; lx += 40 * sc) {
        ctx.beginPath();
        ctx.moveTo(lx, bY + botH / 2 - 2 * sc);
        ctx.lineTo(lx, bY + botH / 2 + 20 * sc);
        ctx.stroke();
      }
      ctx.restore();
      for (let k = bottlesToDraw; k >= -bottlesToDraw; k--) {
        if (k === 0) continue;
        let b_p = p - k * p_spacing;
        if (b_p < 0.03) continue;
        const b_X = start_X + b_p * travel_dist;
        if (b_X < -botW * 3 || b_X > W + botW * 3) continue;
        if (b_p >= 0.9) {
          drawPhaseCapsule(b_X, bY, botW, botH, 1);
        } else {
          drawSingleBottleStage(b_p, b_X, bY, botW, botH);
        }
      }
      const bgFade = p > 0.85 ? clamp((p - 0.85) / 0.15, 0, 1) : 0;
      if (bgFade > 0) {
        ctx.fillStyle = `rgba(5, 4, 3, ${bgFade * 0.92})`;
        ctx.fillRect(0, 0, W, H);
      }
      const hero_X = start_X + p * travel_dist;
      drawSingleBottleStage(p, hero_X, bY, botW, botH);
    }
    animateInteractiveUIPanels(p);
  }
  let targetScrollProg = 0;
  let currentDampedScrollProg = 0;
  const scrollInertiaDamping = 0.05;
  const resetTimelineHandler = () => {
    targetScrollProg = 0;
    currentDampedScrollProg = 0;
    elSlider.value = "0";
  };
  window.addEventListener("reset-timeline", resetTimelineHandler);
  let isAutoplayActive = false;
  const autoplaySpeedStep = 2e-4;
  const wheelHandler = (e) => {
    e.preventDefault();
    const wheelSensitivity = 35e-5;
    targetScrollProg = clamp(targetScrollProg + e.deltaY * wheelSensitivity, 0, 1);
  };
  root.addEventListener("wheel", wheelHandler, { passive: false });
  let touchStartY = 0;
  const touchStartHandler = (e) => {
    touchStartY = e.touches[0].clientY;
    mx = e.touches[0].clientX;
    my = e.touches[0].clientY;
  };
  root.addEventListener("touchstart", touchStartHandler, { passive: true });
  const touchMoveHandler = (e) => {
    if (e.cancelable) e.preventDefault();
    const touchY = e.touches[0].clientY;
    const deltaY = touchStartY - touchY;
    touchStartY = touchY;
    mx = e.touches[0].clientX;
    my = e.touches[0].clientY;
    const touchSensitivity = 1e-3;
    targetScrollProg = clamp(targetScrollProg + deltaY * touchSensitivity, 0, 1);
  };
  root.addEventListener("touchmove", touchMoveHandler, { passive: false });
  const keydownHandler = (e) => {
    const step = 0.015;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      targetScrollProg = clamp(targetScrollProg + step, 0, 1);
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      targetScrollProg = clamp(targetScrollProg - step, 0, 1);
    } else if (e.key === "PageDown" || e.key === " ") {
      e.preventDefault();
      targetScrollProg = clamp(targetScrollProg + step * 3, 0, 1);
    } else if (e.key === "PageUp") {
      e.preventDefault();
      targetScrollProg = clamp(targetScrollProg - step * 3, 0, 1);
    }
  };
  window.addEventListener("keydown", keydownHandler);
  const sliderHandler = (e) => {
    targetScrollProg = parseFloat(e.target.value) / 1e3;
  };
  elSlider.addEventListener("input", sliderHandler);
  const toggleAutoplay = () => {
    isAutoplayActive = !isAutoplayActive;
    if (isAutoplayActive) {
      playIcon.style.display = "none";
      pauseIcon.style.display = "block";
      if (soundEngineActive && sfxAudioCtx && sfxAudioCtx.state === "suspended") {
        sfxAudioCtx.resume();
      }
    } else {
      playIcon.style.display = "block";
      pauseIcon.style.display = "none";
    }
  };
  autoplayBtn.addEventListener("click", toggleAutoplay);
  let renderProg = 0;
  let animationFrameMaster;
  const executeMasterFrame = () => {
    if (isAutoplayActive) {
      targetScrollProg += autoplaySpeedStep;
      if (targetScrollProg > 1) {
        targetScrollProg = 0;
        fireChamberImpulse(600, 3, 0.45, 0.15);
      }
    }
    currentDampedScrollProg += (targetScrollProg - currentDampedScrollProg) * scrollInertiaDamping;
    renderProg = clamp(currentDampedScrollProg, 0, 1);
    renderChronicle(renderProg);
    animationFrameMaster = requestAnimationFrame(executeMasterFrame);
  };
  executeMasterFrame();
  return function cleanup() {
    if (deepLinkTimeout) window.clearTimeout(deepLinkTimeout);
    cancelAnimationFrame(animationFrameMaster);
    window.removeEventListener("reset-timeline", resetTimelineHandler);
    window.removeEventListener("resize", resizeHandler);
    document.removeEventListener("mousemove", globalMouseMoveHandler);
    root.removeEventListener("wheel", wheelHandler);
    root.removeEventListener("touchstart", touchStartHandler);
    root.removeEventListener("touchmove", touchMoveHandler);
    window.removeEventListener("keydown", keydownHandler);
    sensoryBtn.removeEventListener("click", sensoryBtnHandler);
    elSlider.removeEventListener("input", sliderHandler);
    autoplayBtn.removeEventListener("click", toggleAutoplay);
    if (sfxAudioCtx) {
      try {
        sfxAudioCtx.close();
      } catch (e) {
      }
      sfxAudioCtx = null;
    }
  };
}
function bootBottlingChronicle() {
  document.querySelectorAll(".marias-bottling").forEach((rootEl) => {
    const root = rootEl;
    if (root._mboCleanup) return;
    root._mboCleanup = initBottlingChronicle(root);
  });
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bootBottlingChronicle);
} else {
  bootBottlingChronicle();
}
document.addEventListener("shopify:section:unload", ((e) => {
  document.querySelectorAll(".marias-bottling").forEach((rootEl) => {
    const root = rootEl;
    if (root.dataset.sectionId === e.detail.sectionId && root._mboCleanup) {
      root._mboCleanup();
      root._mboCleanup = void 0;
    }
  });
}));
document.addEventListener("shopify:section:load", ((e) => {
  const root = document.querySelector(`.marias-bottling[data-section-id="${e.detail.sectionId}"]`);
  if (root && !root._mboCleanup) {
    root._mboCleanup = initBottlingChronicle(root);
  }
}));
